import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '@/components/ui/Header';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import LoadingState from '@/components/ui/LoadingState';
import { fetchEmployeeReviewDetails } from '@/lib/api/employees';
import { formatDate } from '@/lib/transformers';

const EmployeeReview = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [invitation, setInvitation] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchEmployeeReviewDetails(employeeId);
        if (!isMounted) {
          return;
        }
        setEmployee(data?.employee ?? null);
        setInvitation(data?.invitation ?? null);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        const message =
          err?.response?.data?.message ?? err?.message ?? 'Unable to load employee details.';
        setError(message);
        setEmployee(null);
        setInvitation(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [employeeId]);

  const displayName = useMemo(() => {
    if (!employee) {
      return 'Team Member';
    }
    if (employee?.name && employee.name !== 'Team Member') {
      return employee.name;
    }
    const fullName = employee?.raw?.user?.fullName ?? invitation?.invitedName;
    return fullName || 'Team Member';
  }, [employee, invitation]);

  const normalizedStartDate = useMemo(() => {
    const value = invitation?.startDate ?? employee?.raw?.hireDate ?? employee?.raw?.startDate;
    return formatDate(value) ?? 'Not provided';
  }, [employee, invitation]);

  const onboardingCompletedAt = useMemo(() => {
    return formatDate(invitation?.completedAt) ?? 'Not recorded';
  }, [invitation]);

  const invitationSentAt = useMemo(() => {
    return formatDate(invitation?.invitedAt) ?? 'Not recorded';
  }, [invitation]);

  const birthday = useMemo(() => {
    const value = employee?.raw?.birthday ?? employee?.birthday;
    return value ? formatDate(value) ?? 'Not provided' : 'Not provided';
  }, [employee]);

  const genderLabel = useMemo(() => {
    const value = employee?.gender ?? employee?.raw?.gender;
    if (value === 'male') {
      return 'Male';
    }
    if (value === 'female') {
      return 'Female';
    }
    return 'Not provided';
  }, [employee]);

  const shiftPreference = useMemo(() => {
    const value = employee?.shiftPreference ?? employee?.raw?.shiftPreference;
    if (typeof value === 'string' && value.trim().length > 0 && value !== 'Not specified') {
      return value;
    }
    return 'Not provided';
  }, [employee]);

  const phone = useMemo(() => {
    const value = employee?.phone ?? employee?.raw?.phone;
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : 'Not provided';
  }, [employee]);

  const address = useMemo(() => {
    const value = employee?.address ?? employee?.raw?.address;
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : 'Not provided';
  }, [employee]);

  const emailAddress = useMemo(() => {
    const value = employee?.email ?? employee?.raw?.email ?? invitation?.email;
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : 'Not provided';
  }, [employee, invitation]);

  const assignedLocations = useMemo(() => {
    if (Array.isArray(employee?.locationNames) && employee.locationNames.length > 0) {
      return employee.locationNames;
    }
    if (typeof employee?.location === 'string' && employee.location.trim().length > 0) {
      return [employee.location.trim()];
    }
    return [];
  }, [employee]);

  const statusLabel = useMemo(() => {
    switch (employee?.status) {
      case 'on-work':
        return 'On Work';
      case 'off-work':
        return 'Off Work';
      case 'in-vacation':
        return 'In Vacation';
      case 'holiday':
        return 'Holiday';
      case 'sick-leave':
        return 'Sick Leave';
      default:
        return employee?.status ?? null;
    }
  }, [employee]);

  const statusIndicatorClass = useMemo(() => {
    switch (employee?.status) {
      case 'on-work':
        return 'bg-success';
      case 'off-work':
        return 'bg-muted-foreground';
      case 'in-vacation':
        return 'bg-warning';
      case 'holiday':
        return 'bg-primary';
      case 'sick-leave':
        return 'bg-error';
      default:
        return 'bg-muted-foreground';
    }
  }, [employee]);

  const renderBadges = (items, emptyLabel) => {
    if (Array.isArray(items) && items.length > 0) {
      const sanitized = items
        .map((item) => (typeof item === 'string' ? item.trim() : item))
        .filter((value) => typeof value === 'string' && value.length > 0);

      if (sanitized.length === 0) {
        return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
      }

      return (
        <div className="flex flex-wrap gap-2">
          {sanitized.map((item, index) => (
            <span
              key={`${item}-${index}`}
              className="px-2.5 py-1 text-xs font-medium bg-muted text-muted-foreground rounded-full"
            >
              {item}
            </span>
          ))}
        </div>
      );
    }
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/employee-directory', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header allowLocationSelection={false} />
      <main className="max-w-5xl mx-auto px-6 pt-24 pb-12 space-y-8">
        <div>
          <Button variant="ghost" size="sm" onClick={handleBack} className="mb-4">
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back to directory
          </Button>
        </div>

        {loading ? (
          <div className="bg-card border border-border rounded-xl shadow-elevation-1 p-12">
            <LoadingState
              message="Loading employee details…"
              heightClassName="min-h-[8rem]"
            />
          </div>
        ) : error ? (
          <div className="bg-error/10 border border-error/40 rounded-xl p-8 text-center">
            <Icon name="AlertCircle" size={32} className="text-error mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-error mb-2">Unable to load employee</h2>
            <p className="text-sm text-error-foreground mb-4">{error}</p>
            <Button variant="default" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        ) : !employee ? (
          <div className="bg-card border border-border rounded-xl shadow-elevation-1 p-12 text-center text-muted-foreground">
            No employee data was returned.
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl shadow-elevation-1 overflow-hidden">
            <div className="p-6 border-b border-border bg-muted/20 flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-shrink-0">
                <EmployeeAvatar
                  url={employee?.avatarUrl}
                  name={displayName}
                  alt={`${displayName} profile photo`}
                  status={employee?.status}
                  size="xl"
                  wrapperClassName="border border-border"
                />
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h1 className="text-2xl font-semibold text-foreground">{displayName}</h1>
                  <p className="text-sm text-muted-foreground">
                    Employee ID: {employee?.employeeId}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                  {employee?.role && (
                    <span className="inline-flex items-center gap-1">
                      <Icon name="Briefcase" size={16} />
                      {employee.role}
                    </span>
                  )}
                  {employee?.department && (
                    <span className="inline-flex items-center gap-1">
                      <Icon name="Building2" size={16} />
                      {employee.department}
                    </span>
                  )}
                  {assignedLocations.length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Icon name="MapPin" size={16} />
                      {assignedLocations.join(', ')}
                    </span>
                  )}
                  {statusLabel && (
                    <span className="inline-flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${statusIndicatorClass}`} aria-hidden="true" />
                      Status: {statusLabel}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 space-y-8">
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">Contact information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Email</p>
                    <p className="text-sm text-foreground break-all">{emailAddress}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Phone</p>
                    <p className="text-sm text-foreground">{phone}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Address</p>
                    <p className="text-sm text-foreground">{address}</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">Onboarding details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Start date</p>
                    <p className="text-sm text-foreground">{normalizedStartDate}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Onboarding completed</p>
                    <p className="text-sm text-foreground">{onboardingCompletedAt}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Invitation sent</p>
                    <p className="text-sm text-foreground">{invitationSentAt}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Shift preference</p>
                    <p className="text-sm text-foreground">{shiftPreference}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Birthday</p>
                    <p className="text-sm text-foreground">{birthday}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Gender</p>
                    <p className="text-sm text-foreground">{genderLabel}</p>
                  </div>
                </div>
                {invitation?.message && (
                  <div className="mt-6 bg-muted/30 border border-border rounded-lg p-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      Invitation note
                    </p>
                    <p className="text-sm text-foreground whitespace-pre-line">{invitation.message}</p>
                  </div>
                )}
              </section>

              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">Tags</h2>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Tags
                  </p>
                  {renderBadges(employee?.tags, 'No tags were provided during onboarding.')}
                </div>
              </section>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default EmployeeReview;
